/* @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import { useRetracked } from 'js/lib/retracked';
import type { TrackingProps } from 'js/lib/retracked';
import { useLocation } from 'js/lib/useRouter';

import { Pagination, breakpoints } from '@coursera/cds-core';

import PaginationControls from 'bundles/page/components/cds/PaginationControls';
import { useUserAgent } from 'bundles/page/contexts/UserAgentApiContext';
import type { SearchPagination as PaginationType } from 'bundles/search-common/providers/searchTypes';
import { createSeoURL } from 'bundles/search-common/utils/seoUtils';

type PropsFromCaller = {
  // If true anchor tags will be used for page links over client-side redirects.
  shouldRenderLinks?: boolean;
  paginationData?: PaginationType & { totalPages?: number; page: number };
  currentQuery?: string;
  onPageChange?: () => void;
  activeFilters?: string[];
  setPage: (page: number) => void;
  eventingData?: {
    searchIndex: string;
    searchIndexPosition: number;
  };
  enableScrollToResultsOnPageChange?: boolean;
  resultsPerPage?: number;
};

type PropsToComponent = Omit<PropsFromCaller, 'eventingData'>;

const styles = {
  paginationContainerStyles: () => ({
    '.pagination': {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '3rem',
      marginTop: '3rem',
    },
  }),
};

const clientSideChangePage = (
  nextPage: number,
  setPage: (arg0: number) => void,
  onPageChange: (() => void) | undefined,
  trackComponent: (p: TrackingProps) => void,
  enableScrollToResultsOnPageChange?: boolean
) => {
  setPage(nextPage);
  if (onPageChange) {
    onPageChange();
  }
  if (enableScrollToResultsOnPageChange) {
    const element = document.getElementById('search-results-frame');
    element ? element.scrollIntoView({ behavior: 'smooth' }) : window.scroll(0, 0);
  } else {
    window.scroll(0, 0);
  }
  trackComponent({
    trackingData: { resultsPageNumber: nextPage, page: window.location.pathname },
    trackingName: 'search_results_change_page_number',
    action: 'search',
  });
};

export const SearchResultsPagination = ({
  shouldRenderLinks,
  paginationData,
  currentQuery,
  onPageChange,
  setPage,
  activeFilters,
  enableScrollToResultsOnPageChange,
  resultsPerPage,
}: PropsToComponent) => {
  const userAgent = useUserAgent();
  const trackComponent = useRetracked();
  const location = useLocation();
  const paginationContainerStyles = {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '3rem',
    marginTop: '3rem',
  };

  if (paginationData) {
    const onPaginationChange = (_event: React.ChangeEvent<unknown>, pageNumber: number) =>
      clientSideChangePage(pageNumber, setPage, onPageChange, trackComponent, enableScrollToResultsOnPageChange);
    const pageLinkProps = {
      renderLinks: true,
      algoliaCreatePaginateLink: (pageNumber: number) => {
        const query = createSeoURL(currentQuery || '', pageNumber, activeFilters);

        const searchParams = new URLSearchParams(query);
        const queryString = searchParams.toString();
        let hash = '';
        if (enableScrollToResultsOnPageChange) {
          hash = '#search-results-frame';
        }
        return `${location.pathname}${queryString ? `?${queryString}` : ''}${hash}`;
      },
    };

    return shouldRenderLinks || !paginationData?.totalElements ? (
      <Media query={{ maxWidth: breakpoints.values.sm }} defaultMatches={userAgent && userAgent.isMobileBrowser}>
        {(matches) => (
          <PaginationControls
            pageCount={paginationData.totalPages}
            currentPage={paginationData.page || 1}
            maxPages={matches ? 4 : 5}
            style={paginationContainerStyles}
            largeStyle
            {...pageLinkProps}
          />
        )}
      </Media>
    ) : (
      <div css={styles.paginationContainerStyles}>
        <div className="pagination">
          <Pagination
            total={Math.min(paginationData?.totalElements, 1000)}
            page={paginationData?.page || 1}
            pageSize={resultsPerPage || 12}
            hidePageSizeInput
            onPageChange={onPaginationChange}
          />
        </div>
      </div>
    );
  } else {
    return null;
  }
};

export default compose<PropsToComponent, PropsFromCaller>(
  Retracked.createTrackedContainer<PropsFromCaller>((props) => {
    return {
      indexName: props.eventingData?.searchIndex,
      indexPosition: props.eventingData?.searchIndexPosition || 0,
    };
  })
)(SearchResultsPagination);
